'use client'

import React, { useState, useEffect } from 'react';
import { useToaster } from '../context/ToasterContext';
import Spinner from "../components/Spinner";
import Button from '../components/Button';
import Table from '../components/Table';
import ConfirmationModal from '../components/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../redux/globalDataSlice';

import api from '../api/axios';

// ── Invite Modal ────────────────────────────────────────────────────────────
function InviteUserModal({ open, accountId, onSuccess, onCancel }) {
  const { toaster } = useToaster();
  const [form, setForm] = useState({ name: '', email: '', role: 'SUPPORT' });
  const [submitting, setSubmitting] = useState(false);
  const activeAccount = useSelector((s) => s.globalData.account);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.email || !form.name || !form.role) {
      toaster.error('Name, email and role are required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`${process.env.REACT_APP_BASE_API_URL}/mvapi/people/invite`, {
        email: form.email,
        name: form.name,
        accountId,
        accountName: activeAccount?.name,
        role: form.role,
        isDefault: false,
      });
      toaster.success(`Invite sent to ${form.email}`);
      setForm({ name: '', email: '', role: 'SUPPORT' });
      onSuccess();
    } catch (err) {
      console.error(err);
      toaster.error('Failed to send invite. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Invite Team Member
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Jane Smith"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="jane@example.com"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role
            </label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {['ADMIN', 'DEVELOPER', 'BILLING', 'SUPPORT'].map((r) => (
                <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send Invite'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ───────────────────────────────────────────────────────────────
function EditUserModal({ open, user, onSuccess, onCancel }) {
  const { toaster } = useToaster();
  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);
  const [form, setForm] = useState({ name: '', email: '', role: 'SUPPORT' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      const currentRole = user.accounts?.find(
        (a) => a.accountId === activeAccountId
      )?.role || 'SUPPORT';
      setForm({ name: user.name || '', email: user.email || '', role: currentRole });
    }
  }, [user, activeAccountId]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.email || !form.name || !form.role) {
      toaster.error('Name, email and role are required.');
      return;
    }
    setSubmitting(true);
    try {
      // Update the role on the matching account entry
      const updatedAccounts = (user.accounts || []).map((a) =>
        a.accountId === activeAccountId ? { ...a, role: form.role } : a
      );
      await api.put(
        `${process.env.REACT_APP_BASE_API_URL}/mvapi/people/${user.id}`,
        { ...user, name: form.name, email: form.email, accounts: updatedAccounts }
      );
      toaster.success('User updated successfully.');
      onSuccess();
    } catch (err) {
      console.error(err);
      toaster.error('Failed to update user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Edit User
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Role
            </label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {['ADMIN', 'DEVELOPER', 'BILLING', 'SUPPORT'].map((r) => (
                <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function ManageUsers() {
  const { toaster } = useToaster();
  const dispatch = useDispatch();

  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);
  const currentUserEmail = useSelector((s) => s.auth?.user?.email);

  const [loading, setLoading] = useState(true);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const { users = [], dataLoading } = useSelector((s) => s.globalData);


  useEffect(() => {
    setLoading(dataLoading);
  }, [dataLoading]);

  useEffect(() => {
    if (activeAccountId) dispatch(fetchUsers(activeAccountId));
  }, [activeAccountId, dispatch]);

  // ── Remove user from account ──────────────────────────────────────────────
  const initRemoveUser = (user) => {
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const removeUser = async () => {
    try {
      // Strip this account from the user's accounts array and save
      const updatedAccounts = (selectedUser.accounts || []).filter(
        (a) => a.accountId !== activeAccountId
      );
      await api.put(
        `${process.env.REACT_APP_BASE_API_URL}/mvapi/people/${selectedUser.id}`,
        { ...selectedUser, accounts: updatedAccounts }
      );
      toaster.success(`${selectedUser.name} removed from account.`);
      setShowConfirmModal(false);
      dispatch(fetchUsers(activeAccountId));
    } catch (err) {
      console.error(err);
      toaster.error('Failed to remove user. Please try again.');
    }
  };

  // ── Resend invite ─────────────────────────────────────────────────────────
  const resendInvite = async (user) => {
    try {
      await api.post(`${process.env.REACT_APP_BASE_API_URL}/mvapi/people/invite`, {
        email: user.email,
        name: user.name,
        accountId: activeAccountId,
        isDefault: false,
      });
      toaster.success(`Invite resent to ${user.email}`);
    } catch (err) {
      console.error(err);
      toaster.error('Failed to resend invite. Please try again.');
    }
  };

  const isSelf = (user) =>
    user.email?.toLowerCase() === currentUserEmail?.toLowerCase();

  return (
    <>
      {loading && <Spinner />}

      {!loading && (
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                Manage Users
              </h2>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                View and manage team members who have access to this account.
              </p>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <Button
                type="button"
                variant="primary"
                onClick={() => setShowInviteModal(true)}
              >
                Invite team member
              </Button>
            </div>
          </div>

          <div className="mt-8">
            <Table
              data={users}
              columns={[
                { key: 'name', title: 'Name' },
                { key: 'email', title: 'Email' },
                {
                  key: 'role',
                  title: 'Role',
                  render: (user) => {
                    const role = user.accounts?.find(a => a.accountId === activeAccountId)?.role;
                    return role
                      ? role.charAt(0) + role.slice(1).toLowerCase()
                      : '—';
                  }
                },
                {
                  key: 'actions',
                  title: 'Actions',
                  align: 'right',
                  render: (user) => {
                    const self = isSelf(user);
                    return (
                      <div className="space-x-4">
                        <button
                          type="button"
                          disabled={self}
                          onClick={() => {
                            setSelectedUser(user);
                            setShowEditModal(true);
                          }}
                          className={
                            self
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                              : 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-100'
                          }
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={self}
                          onClick={() => !self && resendInvite(user)}
                          className={
                            self
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                              : 'text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-100'
                          }
                        >
                          Resend Invite
                        </button>
                        <button
                          type="button"
                          disabled={self}
                          onClick={() => !self && initRemoveUser(user)}
                          className={
                            self
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                              : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-100'
                          }
                        >
                          Remove
                        </button>
                      </div>
                    );
                  },
                },
              ]}
              emptyState={{
                title: 'No users found',
                description: 'Invite your first team member to get started.',
              }}
            />
          </div>
        </div>
      )}

      <InviteUserModal
        open={showInviteModal}
        accountId={activeAccountId}
        onSuccess={() => {
          setShowInviteModal(false);
          dispatch(fetchUsers(activeAccountId));
        }}
        onCancel={() => setShowInviteModal(false)}
      />

      <EditUserModal
        open={showEditModal}
        user={selectedUser}
        onSuccess={() => {
          setShowEditModal(false);
          dispatch(fetchUsers(activeAccountId));
        }}
        onCancel={() => setShowEditModal(false)}
      />

      <ConfirmationModal
        open={showConfirmModal}
        title="Remove user"
        description={`Are you sure you want to remove ${selectedUser?.name} from this account? They will lose access immediately.`}
        confirmText="Yes, remove"
        cancelText="Cancel"
        onConfirm={removeUser}
        onCancel={() => setShowConfirmModal(false)}
      />
    </>
  );
}